import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import {
    CartSelfcareSummaryDto,
    GetSelfcareCartApiResponse,
} from 'store/api-slice/hooks'
import {
    BusTicketWithDetails,
    CabanaWithDetails,
    HandlingFee,
    MarchVoucher,
    MemoryCapture,
    Shipping,
    Ticket,
    TicketsBundle,
} from 'store/api-slice/types'
import {
    filterStandaloneTicketsBundlesFromCart,
    filterTicketsWithBundlesFromCart,
} from 'store/cart-slice/helpers'

interface SelfcareCartState {
    isCartCreated: boolean | null
    cartId: string | null
    totalPrice: number | null
    refundPrice: number | null
    newItemsPrice: number | null
    countChanges: number
    countNewItems: number
    items: {
        tickets: Ticket[]
        ticketsBundles: TicketsBundle[]
        busTickets: BusTicketWithDetails[]
        memoryCapture: MemoryCapture | null
        cabanas: CabanaWithDetails[]
        marchVouchers: MarchVoucher[]
        handlingFee: HandlingFee[] | null
        shipping: Shipping | null
    }
    giftCards: CartSelfcareSummaryDto['giftCards']
    payByCardPrice: number | null
    remainingCardValue: number | null
}

const initialState: SelfcareCartState = {
    cartId: null,
    isCartCreated: false,
    countChanges: 0,
    totalPrice: null,
    refundPrice: null,
    newItemsPrice: null,
    countNewItems: 0,
    payByCardPrice: null,
    remainingCardValue: null,
    items: {
        tickets: [],
        ticketsBundles: [],
        busTickets: [],
        memoryCapture: null,
        cabanas: [],
        marchVouchers: [],
        handlingFee: null,
        shipping: null,
    },
    giftCards: [],
}

const SelfcareCartSlice = createSlice({
    name: 'Cart',
    initialState,
    reducers: {
        setIsSelfcareCartCreated(state, action: PayloadAction<boolean>) {
            state.isCartCreated = action.payload
        },
        setSelfcareCart(
            state,
            action: PayloadAction<GetSelfcareCartApiResponse>
        ) {
            const { cart } = action.payload

            if (!cart) return

            const { cartId, items, priceSummary, newItemsQuantity, giftCards } =
                cart
            const {
                countChanges,
                refundPrice,
                totalPrice,
                newItemsPrice,
                payByCardPrice,
                remainingCardValue,
            } = priceSummary || {}

            state.cartId = cartId || null
            state.countChanges = countChanges || 0
            state.refundPrice = refundPrice || null
            state.newItemsPrice = newItemsPrice || null
            state.totalPrice = totalPrice || null
            state.countNewItems = newItemsQuantity || 0
            state.giftCards = giftCards
            state.payByCardPrice = payByCardPrice || null
            state.remainingCardValue = remainingCardValue || 0

            // TICKETS
            state.items.tickets = filterTicketsWithBundlesFromCart(items)
            // STANDALONE TICKETS BUNDLES
            state.items.ticketsBundles =
                filterStandaloneTicketsBundlesFromCart(items)

            // TRANSPORT
            const busTickets =
                (items?.filter(
                    (item) => item.productType === 'bus_ticket'
                ) as BusTicketWithDetails[]) || []

            state.items.busTickets = busTickets

            // MEMORY CAPTURE
            const memoryCapture = items?.find(
                (item) => item.productType === 'memory_capture'
            ) as MemoryCapture

            state.items.memoryCapture = memoryCapture

            // CABANAS
            const cabanas =
                (items?.filter(
                    (item) => item.productType === 'cabana'
                ) as CabanaWithDetails[]) || []

            state.items.cabanas = cabanas

            // MARCH VOUCHERS
            const marchVouchers =
                (items?.filter(
                    ({ productType, detail }) =>
                        productType === 'voucher' &&
                        // TODO: fix issues with cart types
                        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                        // @ts-ignore
                        detail?.voucherType === 'next-visit'
                ) as MarchVoucher[]) || []

            state.items.marchVouchers = marchVouchers

            // HANDLING FEE
            const handlingFee =
                items?.filter((item) => item.productType === 'handling_fee') ||
                []

            state.items.handlingFee = handlingFee

            // SHIPPING
            const shipping =
                items?.find((item) => item.productType === 'shipping') || null

            state.items.shipping = shipping
        },
    },
})

export const { setSelfcareCart, setIsSelfcareCartCreated } =
    SelfcareCartSlice.actions
export default SelfcareCartSlice.reducer
